import {
  DefaultError,
  QueryKey,
  useQuery as tanstackUseQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { DocumentNode } from 'graphql'

import { request } from './client'
import { GraphQlResponse, handleResponse200, ResponseDataShape } from './response'
import { getKey } from './utils'

type Props<Variables extends Record<string, unknown>, ResponseData extends ResponseDataShape> = {
  onCompleted?: (data: ResponseData) => Promise<void>
  options?: Omit<
    undefined | UseQueryOptions<unknown, DefaultError, GraphQlResponse<ResponseData> | void>,
    'queryFn'
  >
  query: DocumentNode
  variables?: Variables
}

export const useQuery = <
  Variables extends Record<string, unknown>,
  ResponseData extends ResponseDataShape,
>({
  onCompleted,
  options,
  query,
  variables,
}: Props<Variables, ResponseData>) => {
  const key: QueryKey = getKey(query)

  return tanstackUseQuery<ResponseData | undefined, DefaultError>({
    queryFn: async (): Promise<ResponseData | undefined> => {
      const response: GraphQlResponse<ResponseData> | void = await request<Variables, ResponseData>(
        {
          key,
          query,
          variables,
        },
      )

      const data: ResponseData | undefined = handleResponse200<ResponseData>({
        response,
      })

      if (onCompleted !== undefined && data !== undefined) {
        onCompleted(data)
      }
      return data
    },
    queryKey: [...key, ...(variables ? [variables] : [])],
    refetchOnWindowFocus: false,
    ...options,
  })
}
